import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ShoppingBag, CreditCard, Gift, Truck } from "tabler-icons-react";
import { useTranslation } from "react-i18next";

const accent = "#43BD98";
const primary = "#2FAE83";

export default function Features() {
	const { t } = useTranslation();
	const section1Ref = useRef(null);
	const section2Ref = useRef(null);
	const isSection1InView = useInView(section1Ref, { once: true, margin: "-100px" });
	const isSection2InView = useInView(section2Ref, { once: true, margin: "-100px" });

	const features = [
		{
			icon: <ShoppingBag size={42} strokeWidth={1.3} />,
			title: t("store.features.cards.0.title"),
			text: t("store.features.cards.0.text"),
		},
		{
			icon: <CreditCard size={42} strokeWidth={1.3} />,
			title: t("store.features.cards.1.title"),
			text: t("store.features.cards.1.text"),
		},
		{
			icon: <Truck size={42} strokeWidth={1.3} />,
			title: t("store.features.cards.2.title"),
			text: t("store.features.cards.2.text"),
		},
		{
			icon: <Gift size={42} strokeWidth={1.3} />,
			title: t("store.features.cards.3.title"),
			text: t("store.features.cards.3.text"),
		},
	];

	const blocks = [
		{
			title: t("store.features.blocks.0.title"),
			text: t("store.features.blocks.0.text"),
			points: t("store.features.blocks.0.points", { returnObjects: true }),
			shadow: "rgba(67,189,152,0.15)",
		},
		{
			title: t("store.features.blocks.1.title"),
			text: t("store.features.blocks.1.text"),
			points: t("store.features.blocks.1.points", { returnObjects: true }),
			shadow: "rgba(67,189,152,0.25)",
		},
	];

	return (
		<section
			id="funcionalidades"
			className="relative pt-20 pb-32 bg-white text-gray-900 overflow-hidden"
			style={{ fontFamily: "'Poppins', sans-serif" }}
		>
			<div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 text-center">
				{/* BLOQUE PRINCIPAL */}
				<div ref={section1Ref} className="max-w-6xl mx-auto mb-28">
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						animate={isSection1InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
						transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
					>
						<p
							className="uppercase tracking-wider font-semibold mb-3"
							style={{ color: accent }}
						>
							{t("store.features.badge")}
						</p>

						<h2
							className="mb-4"
							style={{
								fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
								fontWeight: 800,
								color: "#222",
							}}
						>
							{t("store.features.title")}
						</h2>

						<p className="text-gray-600 text-lg max-w-2xl mx-auto mb-12">
							{t("store.features.subtitle")}
						</p>
					</motion.div>

					<div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
						{blocks.map((item, i) => (
							<motion.div
								key={i}
								initial={{ opacity: 0, y: 25, scale: 0.96 }}
								animate={
									isSection1InView
										? { opacity: 1, y: 0, scale: 1 }
										: { opacity: 0, y: 25, scale: 0.96 }
								}
								transition={{
									duration: 0.6,
									delay: 0.2 + i * 0.15,
									ease: [0.25, 1, 0.5, 1],
								}}
								className="feature-block bg-white border border-gray-200 rounded-3xl p-10 text-left shadow-[0_8px_20px_rgba(0,0,0,0.05)]"
								style={{ ["--shadow"]: item.shadow }}
							>
								<h3 className="text-2xl font-semibold mb-4 text-gray-800">
									{item.title}
								</h3>
								<p className="text-gray-600 mb-6 leading-relaxed">
									{item.text}
								</p>
								<ul className="space-y-3 text-gray-600 text-base list-disc list-inside">
									{item.points.map((p, idx) => (
										<li key={idx}>{p}</li>
									))}
								</ul>
							</motion.div>
						))}
					</div>
				</div>

				{/* BLOQUE 2 – FUNCIONALIDADES */}
				<div ref={section2Ref}>
					<motion.div
						initial={{ opacity: 0, y: 40 }}
						animate={isSection2InView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
						transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
					>
						<p
							className="uppercase tracking-wider font-semibold mb-4"
							style={{ color: accent }}
						>
							{t("store.features.secondaryBadge")}
						</p>

						<h2
							className="mb-12 tracking-tight"
							style={{
								fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
								fontWeight: 800,
								color: "#222",
							}}
						>
							{t("store.features.secondaryTitle.part1")}{" "}
							<span style={{ color: accent }}>
								{t("store.features.secondaryTitle.highlight")}
							</span>
						</h2>
					</motion.div>

					<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
						{features.map((item, i) => (
							<motion.div
								key={item.title}
								className="feature-card bg-white rounded-xl p-6 border border-gray-200 flex flex-col items-center text-center cursor-pointer"
								initial={{ opacity: 0, y: 20 }}
								animate={
									isSection2InView
										? { opacity: 1, y: 0 }
										: { opacity: 0, y: 20 }
								}
								transition={{
									duration: 0.6,
									delay: 0.2 + i * 0.1,
									ease: "easeOut",
								}}
							>
								<div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary/10 mb-4 text-primary">
									{item.icon}
								</div>
								<h3 className="mt-2 font-semibold text-gray-900">
									{item.title}
								</h3>
								<p className="text-sm text-gray-600 mt-2">{item.text}</p>
							</motion.div>
						))}
					</div>
				</div>
			</div>

			<style>{`
				.feature-block {
					transition: transform 0.5s ease, box-shadow 0.5s ease;
				}
				.feature-block:hover {
					transform: scale(1.03) translateY(-4px);
					box-shadow: 0 20px 50px var(--shadow);
				}
				.feature-card {
					transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
				}
				.feature-card:hover {
					transform: scale(1.05) translateY(-6px);
					box-shadow: 0 12px 24px rgba(0,0,0,0.04);
					border-color: ${primary};
				}
				.feature-card:active {
					transform: scale(0.97);
				}
			`}</style>
		</section>
	);
}
